import { Button } from '@nike/eds'
import { useLazyGetGradePropertiesQuery } from 'api/grade-properties'
import { type RootState, useAppSelector } from 'api/redux'
import { UnitDetailsPanel } from 'components/left-detail-panel'
import { MainPanel } from 'components/main-panel'
import { NumberInput } from 'components/number-input/NumberInput'
import { WarningBanner } from 'components/warning-banner/WarningBanner'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  emptyGradeReasonProperties,
  getGradeConfig,
  getGradeReasons,
  type GradeConfig,
  GradeKey,
  type GradeReasonProperties,
  type GradingInstruction,
  type Unit
} from 'types'
import { isFlight, isImpact } from 'utils/dcContext'
import { type ApiHandlerContext } from 'utils/useApiHandler'

import { GradeReasonButtons } from './GradeReasonButtons'
import './Grading.scss'
import './transitions.css'
import { MainGradeButtons } from './MainGradeButtons'

interface GradingProps {
  unitData: Unit
  rasnChannel: string | undefined
  handleReturn: () => void
  updateUnitGradeContext: ApiHandlerContext<Unit>
  useHash: boolean
  useBgrade: boolean
  useSgrade: boolean
  showMheConveyableWarning: boolean
  instruction: GradingInstruction | undefined
}

export const Grading = ({
  unitData,
  rasnChannel,
  handleReturn,
  updateUnitGradeContext,
  useHash,
  useBgrade,
  useSgrade,
  showMheConveyableWarning,
  instruction
}: GradingProps) => {
  const { t } = useTranslation()

  const [grade, setGrade] = useState<GradeKey>(GradeKey.A_GRADE)
  const [gradeReason, setGradeReason] = useState<GradeReasonProperties>(emptyGradeReasonProperties)
  const [quantity, setQuantity] = useState<number>(1)
  const [clicked, setClicked] = useState(false)
  const [displayMainGrades, setDisplayMainGrades] = useState(true)
  const acceptableGrades: string[] = instruction?.acceptableGrades ?? ['A_GRADE', 'B_GRADE', 'C_GRADE', 'HASH', 'S_GRADE']
  const gradeConfigs = useAppSelector((state: RootState) => state.application.gradeConfigs)
  const gradeProperties = useAppSelector((state: RootState) => state.application.gradeReasonProperties)

  // Fetch grade properties based on unit's division (for division-specific C-grade reason codes)
  const [triggerGetGradeProperties] = useLazyGetGradePropertiesQuery()

  useEffect(() => {
    // Fetch grade properties - default to APPAREL if division is unknown
    triggerGetGradeProperties({ division: unitData.division || 'APPAREL' })
  }, [unitData.division, triggerGetGradeProperties])

  // If the unit is already graded the user came from a next screen
  // Displaying the grade and reason which was previously selected
  useEffect(() => {
    const config: GradeConfig | undefined = getGradeConfig(grade, gradeConfigs)
    if (config?.needsReason) {
      setGradeReason({
        grade: unitData.grade,
        display: config.display,
        sequence: -1,
        isegCode: unitData.isegCode,
        reason: unitData.gradeReason,
        reasonCode: unitData.gradeReasonCode,
        refundApprovalRasnChannels: unitData.refundApproval ? [rasnChannel ?? ''] : [''],
        qualityCode: unitData.qualityCode
      })
      setDisplayMainGrades(false)
    }
  }, [])

  // Prevents previously selected grade from triggering the grading action, required for returning from a next screen
  useEffect(() => {
    if (!clicked) return
    const config: GradeConfig | undefined = getGradeConfig(grade, gradeConfigs)
    if (grade && config && (!config.needsReason || gradeReason.reasonCode !== '')) {
      performGrade(gradeReason)
    }
  }, [clicked, grade, gradeReason])

  const performGrade = (gradeData: GradeReasonProperties) => {
    const channelMatch = gradeData.refundApprovalRasnChannels.includes(rasnChannel ?? '')
    const refundApproval = (isFlight || isImpact)
      ? (gradeData.refundColorsOn === undefined || gradeData.refundColorsOn === null || gradeData.refundColorsOn) && channelMatch
      : channelMatch
    const config = getGradeConfig(grade, gradeConfigs)
    // Use the grade state variable to ensure consistency
    // For grades that don't need reasons, explicitly clear reason fields
    const needsReason = config?.needsReason ?? false

    const updatedUnit = {
      ...unitData,
      grade, // Use state variable instead of gradeData.grade to prevent race conditions
      qualityCode: gradeData.qualityCode,
      isegCode: gradeData.isegCode,
      gradeReason: needsReason ? gradeData.reason : '',
      gradeReasonCode: needsReason ? gradeData.reasonCode : '',
      refundApproval,
      quantity,
      status: 'GRADED'
    }
    if (!updateUnitGradeContext.isLoading) {
      updateUnitGradeContext.handleApiCall(updatedUnit).then()
    }
  }

  const handleGradeClick = (newGrade: GradeKey) => {
    setGrade(newGrade)
    const config = getGradeConfig(newGrade, gradeConfigs)

    if (config?.needsReason) {
      setDisplayMainGrades(false)
    } else {
      // For grades that don't need reasons (A_GRADE, HASH, S_GRADE),
      // explicitly set grade properties and clear any stale reason fields
      const gradeConfig = getGradeReasons(newGrade, gradeProperties)[0]
      setGradeReason({
        grade: newGrade,
        display: config?.display ?? '',
        sequence: gradeConfig?.sequence ?? 0,
        qualityCode: gradeConfig?.qualityCode ?? '',
        isegCode: gradeConfig?.isegCode ?? '',
        reasonCode: '', // Explicitly clear to prevent stale B_GRADE/C_GRADE reason codes
        reason: '', // Explicitly clear to prevent stale reason descriptions
        refundApprovalRasnChannels: gradeConfig?.refundApprovalRasnChannels ?? []
      })
      setClicked(true)
    }
  }

  const handleGradeReasonClick = (reason: GradeReasonProperties) => {
    setGradeReason(reason)
    setClicked(true)
  }

  const handleBackToMainGrades = () => {
    setDisplayMainGrades(true)
    setGradeReason(emptyGradeReasonProperties)
  }

  const leftPanelContent = <UnitDetailsPanel unit={unitData} showImage={true}/>

  const checkIfHashable = () => {
    return useHash && unitData.hashable && acceptableGrades.includes('HASH')
  }

  const checkIfBGradable = () => {
    return useBgrade && acceptableGrades.includes('B_GRADE')
  }

  const checkIfSGradable = () => {
    return useSgrade && acceptableGrades.includes('S_GRADE')
  }

  console.log('MHE Debug:', { showMheConveyableWarning, mheConveyable: unitData.mheConveyable })

  const mainPanelContent = <div className='flex flex-col flex-grow justify-between w-3/5'>
    <div className="flex-grow flex flex-col">
      {showMheConveyableWarning && unitData.mheConveyable === false && <WarningBanner>{t('warnings.mhe_non_conveyable')}</WarningBanner>}
      {instruction && <WarningBanner>{instruction.value}</WarningBanner>}

      {/* empty for flex spacing */}
      <div className="flex-grow" />

      <div className="flex flex-col">
        {displayMainGrades
          ? (
              <div key={'main-grades'} className={'animated fadeInBottom mb-24'}>
                <MainGradeButtons
                  activeGrade={grade}
                  handleGradeChange={handleGradeClick}
                  hashable={checkIfHashable()}
                  aGradable={acceptableGrades.includes('A_GRADE')}
                  bGradable={checkIfBGradable()}
                  cGradable={acceptableGrades.includes('C_GRADE')}
                  sGradable={checkIfSGradable()}
                  instruction={instruction}/>
              </div>
            )
          : (
              <div key={grade} className={'animated fadeInBottom mb-5'}>
                <GradeReasonButtons
                  gradeReason={gradeReason}
                  reasons={getGradeReasons(grade, gradeProperties)}
                  setGradeReason={handleGradeReasonClick}
                  gradeText={getGradeConfig(grade, gradeConfigs)!.display}
                  hasInstruction={!!instruction}
                  rasnChannel={rasnChannel!}/>
              </div>
            )}

        <div className='flex justify-between w-100'>
          <Button className='text-4xl' onClick={() => {
            !displayMainGrades && getGradeConfig(grade, gradeConfigs)?.needsReason ? handleBackToMainGrades() : handleReturn()
          }} variant='secondary'>
            {t('buttons.return')}
          </Button>
          {rasnChannel !== 'DIGITAL' && <NumberInput value={quantity} setValue={setQuantity} minimum={1}/>}
        </div>
      </div>
    </div>
  </div>

  return <MainPanel leftPanelContent={leftPanelContent} mainPanelContent={mainPanelContent}/>
}
